import React from "react"
import {
  Typography,
  Box,
  Button,
  Pagination as MUIPagination,
  PaginationItem,
  Breakpoint
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { StudentFilter } from "../../../../practice-testing/helpers/react/StudentFilter"
import { lightBlue } from "../../../../snapcourse/themes/palette"

const useStyles = makeStyles()(({ palette, breakpoints, spacing, mixins }) => ({
  tablePagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: spacing(2),

    [breakpoints.down("sm" as Breakpoint)]: {
      flexDirection: "column",
      fontSize: 12
    }
  },
  filterContainer: {
    marginLeft: spacing(1),
    display: "flex",
    alignItems: "center"
  },
  selectedPaginationItem: {
    "&.Mui-selected": {
      backgroundColor: lightBlue[600],
      color: palette.common.white
    },
    "&.Mui-selected:hover": {
      backgroundColor: lightBlue[800],
      color: palette.common.white
    },
    "&.Mui-selected:focus": {
      ...mixins.focusRing.outline,
      backgroundColor: lightBlue[800],
      color: palette.common.white
    },
    "&:focus": {
      ...mixins.focusRing.outline,
      backgroundColor: lightBlue[800],
      color: palette.common.white
    }
  },
  root: {
    "& ul": {
      flexWrap: "nowrap",
      "& li": {
        "&:first-of-type": {
          flexBasis: "100%",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center"
        },
        "&:last-child": {
          flexBasis: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center"
        }
      }
    }
  },
  filterTitle: {
    color: palette.primary[500],
    marginRight: spacing(3)
  }
}))

const getRowFilterOptions = (customPageSizes = [10, 20, 50, 100]) => {
  return customPageSizes.map(pageSize => ({
    id: pageSize,
    label: pageSize
  }))
}

interface Props {
  currentPage: number
  pageCount: number
  noOfItems?: number
  siblingCount?: number
  customPageSizes?: number[]
  compact?: boolean
  onChangeNoItems?: (e: React.ChangeEvent) => void
  onPageChange: (e: React.SyntheticEvent, selected: number) => void
  outerClasses?: {
    container?: string
  }
}

const Pagination: React.FC<React.PropsWithChildren<Props>> = props => {
  const {
    pageCount,
    currentPage,
    siblingCount,
    noOfItems,
    customPageSizes,
    compact = false,
    onChangeNoItems,
    onPageChange,
    outerClasses
  } = props
  const { cx, classes } = useStyles()

  return (
    <Box
      className={cx(classes.tablePagination, outerClasses?.container)}
      data-automation-id="pagination"
      sx={{
        overflowX: "auto",
        "& > *": {
          minWidth: "fit-content"
        }
      }}
    >
      {noOfItems && onChangeNoItems && (
        <Box className={classes.filterContainer}>
          <Box component="span" className={classes.filterTitle}>
            <Typography variant="body2" color="inherit">
              Rows per page
            </Typography>
          </Box>
          <StudentFilter
            option={getRowFilterOptions(customPageSizes)}
            filterName={""}
            onChange={onChangeNoItems}
            selectedOption={noOfItems}
            aria-label="Rows per page"
          />
        </Box>
      )}
      <MUIPagination
        classes={{
          root: classes.root
        }}
        shape="rounded"
        count={pageCount}
        page={currentPage}
        defaultPage={1}
        siblingCount={compact ? 0 : siblingCount ?? 0}
        boundaryCount={compact ? 0 : 1}
        onChange={onPageChange}
        renderItem={item => {
          if (item.type === "previous") {
            return (
              <Button
                startIcon={<ChevronLeft />}
                onClick={item.onClick}
                disabled={item.disabled}
                data-automation-id="pagination-prev-button"
                disableRipple
              >
                Prev
              </Button>
            )
          } else if (item.type === "next") {
            return (
              <Button
                endIcon={<ChevronRight />}
                onClick={item.onClick}
                disabled={item.disabled}
                data-automation-id="pagination-next-button"
                disableRipple
              >
                Next
              </Button>
            )
          } else {
            return (
              <PaginationItem
                {...item}
                className={classes.selectedPaginationItem}
                data-automation-id="pagination-item"
              />
            )
          }
        }}
      />
    </Box>
  )
}

export default Pagination
